import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useWorkflow,
  useConfig,
  WorkflowPageLayout,
  formatPhoneLabel,
  safeHttpUrl,
  sendInternalAcceptanceNotification,
} from '@sluitstraat/core';
import { endPageStyles as styles } from '../../styles/commonStyles';
import { clearSaaSessionState } from '../../utils/workflowReset';
import { WorkflowDebugPanel } from '../../components/WorkflowDebugPanel';
import { buildPremiumDebugSection, readDebugIds, readEnrichedDocument, collectProducts, readCreateRequestDebug } from '../../utils/debugData';
import { hasProvisionalCover, hasMotorProduct, PROVISIONAL_COVER_EXCLUSION_GROUPS } from '../../config/provisionalCover';
import { sendSaaConfirmationEmail, sendSaaReviewEmail } from '../../email/sendSaaConfirmationEmail';
import { hasAnySelectedProduct } from '../../utils/productGuards';

const EndPageScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { config } = useConfig();
  const { data, paymentData, selectedProducts } = useWorkflow();
  const [showConfetti, setShowConfetti] = useState(false);
  const emailSentRef = useRef(false); // Track if email has been sent in this component instance

  // End-state routing (centralised in config/provisionalCover.ts). The applicant has provisional
  // cover unless a data-backed exclusion is triggered (justice / fraude / opzegging answered "ja").
  //   provisional cover  -> accepted page + confirmation email (voorlopige-dekking model).
  //   no provisional cover -> in-behandeling (review) page + review email, which keep the
  //                           "nog niet verzekerd" wording (correct there — no provisional cover).
  // NOTE: this replaces the old "any acceptance answer = J" rule; the schade question (RI_EGD)
  // routes on any "ja", but the remaining overige questions still don't (see provisionalCover.ts).
  const acceptanceAnswers = data?.['acceptance-questions']?.answers || {};
  const isReviewMode = !hasProvisionalCover(acceptanceAnswers);
  const showRijontzegging = hasMotorProduct(selectedProducts);

  // Get user's email and start date from workflow data
  const userEmail = data?.['client-information']?.clientInfo?.email || '';
  const startDate = paymentData?.paymentInfo?.startDate || '';

  // Get contact info from config.json
  const contactPhone = config?.texts?.contact?.phone || config?.customer?.contact?.phone?.main || '';
  const contactEmail = config?.texts?.contact?.email || config?.customer?.contact?.email || '';

  const confettiPieces = useMemo(() => {
    const colors = ['#ffd700', '#ff69b4', '#00bfff', '#7fff00', '#ff6347', '#9333ea', '#ec4899', '#f59e0b'];
    return [...Array(150)].map(() => ({
      left: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 2}s`,
      animationDuration: `${3 + Math.random() * 2}s`,
      backgroundColor: colors[Math.floor(Math.random() * colors.length)],
    }));
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Validate that request was submitted - redirect if accessed directly or on reload
  useEffect(() => {
    // First check: Verify workflow context has actual data (resets on page reload)
    // This catches direct URL access and page reloads
    // Generic — a hardcoded per-product list here silently blocks any product not
    // enumerated, e.g. doorlopendereis.
    const hasSelectedProducts = hasAnySelectedProduct(selectedProducts);
    const hasClientInfo = data?.['client-information']?.clientInfo?.email;

    if (!hasSelectedProducts || !hasClientInfo) {
      // Workflow context is empty - this is a fresh page load, not normal navigation
      navigate('/workflow/product-selection', { replace: true });
      return;
    }

    // Second check: Verify sessionStorage has required IDs
    const relationId = sessionStorage.getItem('vp_idnr');
    const quoteId = sessionStorage.getItem('quote_id');

    if (!relationId || !quoteId) {
      navigate('/workflow/product-selection', { replace: true });
      return;
    }

    // Third check: Verify request was successfully submitted
    const requestSubmittedKey = `request_submitted_${relationId}_${quoteId}`;
    const requestSubmitted = sessionStorage.getItem(requestSubmittedKey);

    if (requestSubmitted !== 'true') {
      navigate('/workflow/confirmation', { replace: true });
      return;
    }
  }, [selectedProducts, data, navigate]); // Include dependencies

  // "Terug naar landingspagina": leaves the app for the brand's landing page (the url the
  // header logo used to carry; the logo now stays in-app). Every SAA session key goes first so a
  // return to the app starts clean. A new aanvraag from here = the logo (reset + product selection).
  // Brand-config url into window.location: http(s) only.
  const landingUrl = safeHttpUrl(config.branding?.logoLinkUrl);
  const backToLanding = () => {
    if (!landingUrl) return;
    clearSaaSessionState();
    window.location.href = landingUrl;
  };

  // Trigger confetti animation on mount (only in confirmation mode) and clear workflow session
  useEffect(() => {
    let confettiTimer: ReturnType<typeof setTimeout> | undefined;
    if (!isReviewMode) {
      setShowConfetti(true);
      confettiTimer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
    }

    // Send confirmation email once (only if not already sent)
    const sendConfirmationEmail = async () => {
      try {
        // Check useRef flag first - prevents duplicate calls within same component instance
        if (emailSentRef.current) {
          // console.log('[EndPage] Email already sent in this session (useRef), skipping');
          return;
        }

        // Get IDs from sessionStorage
        const relationId = sessionStorage.getItem('vp_idnr');
        const quoteId = sessionStorage.getItem('quote_id');
        const requestId = sessionStorage.getItem('request_id');

        // Check if request was submitted using relation_id + quote_id
        // This is more reliable than request_id which might not be in the response
        if (!relationId || !quoteId) {
          // console.warn('[EndPage] Missing relation or quote ID, cannot send email');
          return;
        }

        // Check if request was successfully submitted (flag set in ConfirmationScreen)
        const requestSubmittedKey = `request_submitted_${relationId}_${quoteId}`;
        const requestSubmitted = sessionStorage.getItem(requestSubmittedKey);

        if (requestSubmitted !== 'true') {
          // console.warn('[EndPage] Request not submitted flag not found, skipping email');
          return;
        }

        // Check if email was already sent for this request (in sessionStorage)
        const emailSentKey = `email_sent_${relationId}_${quoteId}`;
        const emailAlreadySent = sessionStorage.getItem(emailSentKey);

        if (emailAlreadySent === 'true') {
          // console.log('[EndPage] Email already sent for relation:', relationId, 'quote:', quoteId);
          emailSentRef.current = true; // Update ref to match sessionStorage
          return;
        }

        // Validate client email is present before trying to send
        const clientEmail = data?.['client-information']?.clientInfo?.email;
        if (!clientEmail) {
          console.warn('[EndPage] Client email not found in workflow data, skipping email send');
          return;
        }

        // Mark as sending (before the call to prevent race conditions)
        emailSentRef.current = true;

        // Send the email (non-blocking - don't prevent page from displaying on error)
        // Use request_id if available, otherwise use relation_id
        const emailRequestId = requestId || relationId;
        // console.log('[EndPage] Sending confirmation email, request ID:', emailRequestId);

        // Build question text lookup from i18n — used by both confirmation and
        // review emails (and the internal notification) so all answered questions
        // can be listed with a Yes/No badge regardless of mode.
        const questionTexts: Record<string, string> = {};
        const questionIds = ['RI_EGD', 'RI_OGB', 'RI_POLJUS', 'RI_OVRMED', 'RI_CGGDA', 'RI_STVOOP', 'US_AANSPRP', 'RI_SCH5JR', 'RI_BGP', 'RI_BYZVRWE', 'RI_DGP', 'RI_GEWEIGE', 'RI_NGP', 'RI_STRAFRV'];
        questionIds.forEach(id => {
          const text = t(`screens.acceptanceQuestions.questions.${id}`);
          if (text && !text.includes('screens.acceptanceQuestions')) questionTexts[id] = text;
        });

        // Confirmation page checkbox labels — mirrored into every email so
        // recipients can see which declarations were accepted. The on-screen
        // checkbox texts lean on surrounding paragraphs for context, so the
        // emails use longer standalone labels from `emailConsents`.
        const confirmationLabels = {
          terms: t('screens.confirmation.emailConsents.terms'),
          privacy: t('screens.confirmation.emailConsents.privacy'),
          sepa: t('screens.confirmation.emailConsents.sepa'),
        };

        // Both paths render the SAA-only design (living entirely in the SAA app, never touching
        // core): the confirmation for provisional cover, and the review variant (in-behandeling,
        // "nog niet verzekerd" + the yes-answered vragen) when provisional cover is excluded.
        if (isReviewMode) {
          await sendSaaReviewEmail(data, paymentData, emailRequestId, config, questionTexts, selectedProducts, confirmationLabels, t);
        } else {
          await sendSaaConfirmationEmail(data, paymentData, emailRequestId, config, selectedProducts, t);
        }

        // Mark email as sent in sessionStorage to prevent duplicates on page reload
        sessionStorage.setItem(emailSentKey, 'true');

        try {
          await sendInternalAcceptanceNotification(data, emailRequestId, acceptanceAnswers, config, paymentData, confirmationLabels);
          // console.log('[EndPage] Internal acceptance notification sent successfully');
        } catch (error) {
          // Silently fail - internal notification is not critical
          // const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          // console.log('[EndPage] Internal notification not sent (no yes answers or failed):', errorMessage);
        }
      } catch (error) {
        // Log error but don't show to user - email is nice-to-have, not critical
        console.error('[EndPage] Failed to send confirmation email:', error);
        // Reset ref on error so retry is possible
        emailSentRef.current = false;
      }
    };

    // Send email asynchronously (don't await to avoid blocking page render)
    sendConfirmationEmail();

    // Clear workflow session flags so next workflow will be treated as fresh
    sessionStorage.removeItem('workflow_session_active');
    sessionStorage.removeItem('workflow_current_page');
    // Mark workflow as completed - ProductSelectionScreen will check this to reset IDs
    sessionStorage.setItem('workflow_completed', 'true');

    return () => { if (confettiTimer) clearTimeout(confettiTimer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot send keyed on data; emailSentRef guards repeats, config/t/selection are read at that moment
  }, [data]);

  // Collected page data + the createRequest request/response (persisted by the Confirmation
  // screen). Both JSON.parse a full contract document out of sessionStorage, so this only runs
  // when the panel below actually renders — the same DEV gate keeps the work (and the payloads)
  // out of a production build instead of doing it on every render and throwing it away.
  const debug = import.meta.env.DEV
    ? (() => {
        const { request, response } = readCreateRequestDebug();
        const collected: Record<string, unknown> = {
          ids: readDebugIds(),
          relation: data?.['client-information']?.clientInfo ?? null,
          payment: data?.['payment-information-request'] ?? paymentData?.paymentInfo ?? null,
          premium: buildPremiumDebugSection(selectedProducts, data, config),
          package: readEnrichedDocument(),
          products: collectProducts(selectedProducts, data),
        };
        return { request, response, collected };
      })()
    : null;

  return (
    <WorkflowPageLayout currentStep={0} totalSteps={0} flowType="application" hideProgress={true}>
      <main style={styles.page}>
        {/* Confetti Animation */}
        {showConfetti && (
        <div style={styles.confettiContainer}>
          {confettiPieces.map((piece, i) => (
            <div
              key={i}
              style={{
                ...styles.confetti,
                left: piece.left,
                animationDelay: piece.animationDelay,
                animationDuration: piece.animationDuration,
                backgroundColor: piece.backgroundColor,
              }}
            />
          ))}
        </div>
      )}

      <div style={styles.container} className="end-page-container">
        <div style={styles.successIcon}>
          {isReviewMode ? (
            <svg style={styles.checkmarkIconReview} viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          ) : (
            <svg style={styles.checkmarkIcon} viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
              <path d="m9 11 3 3L22 4"></path>
            </svg>
          )}
        </div>

        <h1 style={styles.title} className="end-page-title">
          {isReviewMode ? t('screens.endPage.review.title') : t('screens.endPage.title')}
        </h1>
        <p style={styles.subtitle} className="end-page-subtitle">
          {isReviewMode ? t('screens.endPage.review.subtitle') : t('screens.endPage.subtitle')}
        </p>

        <div style={styles.card} className="end-page-card">
          <h2 style={styles.cardTitle}>
            {isReviewMode ? t('screens.endPage.review.whatHappensNext.title') : t('screens.endPage.whatHappensNext.title')}
          </h2>

          <div style={styles.stepsList}>
            <div style={styles.step} className="end-page-step">
              <div style={isReviewMode ? styles.stepNumberReview : styles.stepNumber}>1</div>
              <div style={styles.stepContent}>
                <h3 style={styles.stepTitle}>
                  {isReviewMode ? t('screens.endPage.review.whatHappensNext.step1.title') : t('screens.endPage.whatHappensNext.step1.title')}
                </h3>
                <p style={styles.stepText}>
                  {isReviewMode ? t('screens.endPage.review.whatHappensNext.step1.text') : t('screens.endPage.whatHappensNext.step1.text', { email: userEmail })}
                </p>
              </div>
            </div>

            <div style={styles.step} className="end-page-step">
              <div style={isReviewMode ? styles.stepNumberReview : styles.stepNumber}>2</div>
              <div style={styles.stepContent}>
                <h3 style={styles.stepTitle}>
                  {isReviewMode ? t('screens.endPage.review.whatHappensNext.step2.title') : t('screens.endPage.whatHappensNext.step2.title')}
                </h3>
                <p style={styles.stepText}>
                  {isReviewMode ? t('screens.endPage.review.whatHappensNext.step2.text') : t('screens.endPage.whatHappensNext.step2.text')}
                </p>
              </div>
            </div>

            <div style={styles.step} className="end-page-step">
              <div style={isReviewMode ? styles.stepNumberReview : styles.stepNumber}>3</div>
              <div style={styles.stepContent}>
                <h3 style={styles.stepTitle}>
                  {isReviewMode ? t('screens.endPage.review.whatHappensNext.step3.title') : t('screens.endPage.whatHappensNext.step3.title')}
                </h3>
                <p style={styles.stepText}>
                  {isReviewMode ? t('screens.endPage.review.whatHappensNext.step3.text') : t('screens.endPage.whatHappensNext.step3.text', { startDate: startDate })}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* "Let op" provisional-cover exclusions — only on the accepted (provisional-cover) page,
            in the same amber/yellow style as the Slotvragen "Belangrijk" box. Groups + which
            bullets show are driven by config/provisionalCover.ts; the rijontzegging bullet shows
            only when a motor product is in the application. The betalingsachterstand and 5+ schades
            bullets are standard informational text (not data-backed routing). */}
        {!isReviewMode && (
          <div style={styles.importantNotice} className="end-page-card">
            <div style={styles.noticeHeader}>
              <svg style={styles.noticeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <strong style={styles.noticeTitle}>{t('screens.endPage.provisionalCover.title')}</strong>
            </div>
            {PROVISIONAL_COVER_EXCLUSION_GROUPS.map((group, gi) => {
              const bullets = group.bullets.filter((b) => !b.requiresAuto || showRijontzegging);
              return (
                <div key={gi}>
                  <p style={styles.noticeText}>{t(group.introKey)}</p>
                  <ul style={styles.noticeList}>
                    {bullets.map((b) => (
                      <li key={b.i18nKey} style={styles.noticeListItem}>
                        {t(b.i18nKey)}
                        {b.subBulletKey && (
                          <ul style={styles.noticeSubList}>
                            <li>{t(b.subBulletKey)}</li>
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {landingUrl && (
          <div style={styles.newApplicationActions}>
            <button type="button" style={styles.newApplicationButton} onClick={backToLanding}>
              {t('screens.endPage.backToLanding')}
            </button>
          </div>
        )}

        <div style={styles.infoCard} className="end-page-card">
          <h3 style={styles.infoTitle}>{t('screens.endPage.needHelp.title')}</h3>
          <p style={styles.infoText}>{t('screens.endPage.needHelp.text')}</p>
          <div style={styles.contactInfo} className="end-page-contact-info">
            <div style={styles.contactItem}>
              <svg style={styles.contactIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
              </svg>
              <a href={`tel:${contactPhone}`} style={styles.contactLink}>
                {formatPhoneLabel(contactPhone)}
              </a>
            </div>
            <div style={styles.contactItem}>
              <svg style={styles.contactIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              <a href={`mailto:${contactEmail}`} style={styles.contactLink}>
                {contactEmail}
              </a>
            </div>
          </div>
        </div>

        {debug && (
          <WorkflowDebugPanel
            collected={debug.collected}
            request={debug.request}
            response={debug.response}
          />
        )}
      </div>
      </main>
    </WorkflowPageLayout>
  );
};

export default EndPageScreen;
